import React, { useState } from "react";
import { ArrowsClockwise, Broadcast, Lightning, Info } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { toast } from "sonner";

function timeAgo(iso) {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  return `${h}h ago`;
}

export function LiveBadge({ source, meta, onRefreshed }) {
  const [refreshing, setRefreshing] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const isLive = source === "live";
  const planTier = meta?.plan_tier;
  const planLabel =
    planTier === "paid" || planTier === "paid_bet365"
      ? "Player Props"
      : planTier === "free"
      ? "Game Totals (free tier)"
      : "—";

  const refresh = async (redetect = false) => {
    setRefreshing(true);
    try {
      if (redetect) {
        // Re-detect plan: forces an actual API call to probe markets
        const { data } = await api.post("/odds/refresh?wait=true&redetect=true");
        toast("Plan re-detected", {
          description: `Plan: ${data?.meta?.plan_tier || "unknown"}`,
        });
      } else {
        // Plain refresh = re-read from server cache only, no Odds API credits used
        toast("Refreshed", { description: "Re-pulled latest cached picks." });
      }
      onRefreshed?.();
    } catch (e) {
      toast.error("Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="flex items-center gap-3 flex-wrap" data-testid="live-badge">
      <div
        className="flex items-center gap-2 px-3 h-9 border"
        style={{
          borderColor: isLive ? "#FF4500" : "#27272A",
          background: isLive ? "rgba(255,69,0,0.08)" : "#121212",
        }}
      >
        {isLive ? (
          <Broadcast size={14} weight="fill" color="#FF4500" />
        ) : (
          <Lightning size={14} weight="fill" color="#A1A1AA" />
        )}
        <span
          className="overline"
          style={{ color: isLive ? "#FF4500" : "#A1A1AA", letterSpacing: "0.2em" }}
        >
          {isLive ? "Live" : "Demo Data"}
        </span>
        {meta?.quota_remaining != null && isLive && (
          <span className="font-data text-xs text-[#A1A1AA] pl-2 border-l border-[#27272A]">
            {meta.quota_remaining} credits left
          </span>
        )}
      </div>

      {isLive && (
        <div className="overline text-[#A1A1AA]" data-testid="live-updated">
          Updated {timeAgo(meta?.updated_at)}
        </div>
      )}

      {isLive && planTier && (
        <div
          className="overline px-2 py-1 border border-[#27272A]"
          data-testid="live-plan"
          style={{ color: "#A1A1AA" }}
        >
          PLAN · {planLabel}
        </div>
      )}

      <button
        onClick={() => refresh(false)}
        disabled={refreshing}
        className="flex items-center gap-1.5 px-3 h-9 border border-[#27272A] hover:border-white/30 transition-colors disabled:opacity-50"
        data-testid="refresh-odds-btn"
      >
        <ArrowsClockwise
          size={14}
          className={refreshing ? "animate-spin" : ""}
        />
        <span className="overline">{refreshing ? "Pulling…" : "Refresh"}</span>
      </button>

      <button
        onClick={() => setShowInfo((v) => !v)}
        className="text-[#A1A1AA] hover:text-white transition-colors"
        aria-label="About live data"
        data-testid="live-info-btn"
      >
        <Info size={16} />
      </button>

      {showInfo && (
        <div
          className="fixed inset-0 z-40 bg-black/60 flex items-center justify-center p-4"
          onClick={() => setShowInfo(false)}
        >
          <div
            className="max-w-md w-full bg-[#0A0A0A] border border-[#27272A] p-6"
            onClick={(e) => e.stopPropagation()}
            data-testid="live-info-modal"
          >
            <div className="overline mb-3">About this data</div>
            <h3 className="font-head text-2xl text-white mb-3">Beta: Game-Total Lines</h3>
            <ul className="text-sm text-[#A1A1AA] space-y-2 leading-relaxed">
              <li>• Live odds are pulled from <span className="text-white">The Odds API</span> with auto-detected plan tier.</li>
              <li>• <span className="text-white">Free tier:</span> game over/under totals (DraftKings, FanDuel).</li>
              <li>• <span className="text-white">Paid tier:</span> real player props (points, yards, hits, shots).</li>
              <li>• Last-10 trend data is simulated for the beta until a stats provider is wired in.</li>
            </ul>
            <div className="mt-5 flex items-center gap-4">
              <a
                href="https://the-odds-api.com/#get-access"
                target="_blank"
                rel="noopener noreferrer"
                className="overline text-[#FF4500] hover:underline"
                data-testid="upgrade-link"
              >
                Upgrade plan →
              </a>
              <button
                onClick={() => { setShowInfo(false); refresh(true); }}
                className="overline text-[#FACC15] hover:underline"
                data-testid="redetect-btn"
              >
                Re-detect plan →
              </button>
              <button
                onClick={() => setShowInfo(false)}
                className="overline hover:text-white ml-auto"
                data-testid="live-info-close"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
